<template>
  <v-container fluid>
    <section>
      <!-- page header -->
      <div class="d-flex align-end flex-wrap mb-4 ap-header-row">
        <div>
          <p class="ap-kicker mb-1">{{ $t("edit_product") }}</p>
          <v-card-title class="py-0 pl-0 text-primary product-page-title">{{
            headline
          }}</v-card-title>
        </div>
        <v-spacer />
        <div class="ap-progress">
          <div class="d-flex justify-space-between ap-progress__labels">
            <span>{{ $t("ready_to_save") }}</span>
            <span>{{ readinessCount }} / {{ readinessTotal }}</span>
          </div>
          <v-progress-linear
            :value="readinessPercent"
            height="4"
            rounded
            color="primary"
            background-color="grey lighten-3"
          />
        </div>
        <v-btn
          text
          small
          color="primary"
          class="px-0 ml-4"
          @click="$router.push('/products')"
        >
          <v-icon small class="mr-1">fal fa-arrow-left</v-icon>
          {{ $t("back") }}
        </v-btn>
      </div>

      <v-row>
        <!-- ============ LEFT: IDENTITY / PRICE / INVENTORY ============ -->
        <div class="col-ms-12 col-md-8 col-lg-8 col-xl-8">
          <v-card class="ap-card mb-4" outlined>
            <v-card-title class="pb-1 product-card-title ap-card-title">
              <span class="ap-card-accent"></span>
              <span>{{ $t("identity") }}</span>
              <span class="ap-card-subtitle">{{
                $t("identity_subtitle")
              }}</span>
            </v-card-title>
            <v-card-text class="pa-7 pt-3">
              <!-- image + localized name, side by side -->
              <div class="ap-identity-row mb-5">
                <div class="ap-identity-row__image">
                  <div class="section-label mb-2">{{ $t("product_image") }}</div>
                  <DragDropUpload
                    v-model="imgFile"
                    accept="image/*"
                    :max-size-mb="5"
                    @file-selected="onFileSelected"
                    @file-removed="onFileRemoved"
                  />
                  <div class="ap-hint mt-1">{{ $t("image_upload_hint") }}</div>
                </div>

                <div class="ap-identity-row__name">
                  <div class="section-label mb-2">{{ $t("product_name") }} <span class="required-mark">*</span></div>
                  <v-btn-toggle
                    v-model="activeLang"
                    mandatory
                    dense
                    class="ps-status-toggle mb-3"
                  >
                    <v-btn
                      v-for="lang in langTabs"
                      :key="lang.code"
                      small
                      :value="lang.code"
                    >
                      {{ lang.code }}
                      <span
                        class="ap-lang-dot ml-1"
                        :class="{ 'ap-lang-dot--filled': lang.filled }"
                      ></span>
                    </v-btn>
                  </v-btn-toggle>
                  <TTextField
                    skip-translation
                    dense
                    v-model="currentNameValue"
                    :errorMessages="activeLang === 'LA' ? nameError : []"
                    @input="activeLang === 'LA' && $v.form.name.$touch()"
                    @blur="activeLang === 'LA' && $v.form.name.$touch()"
                  />

                  <!-- classification: stacked below the name field, beside the image -->
                  <v-row class="mt-3" dense>
                    <v-col cols="12" sm="6">
                      <TTextField
                        v-model="form.code"
                        label="barcode"
                        dense
                        :counter="true"
                        :maxlength="20"
                        :errorMessages="codeError"
                        @input="$v.form.code.$touch()"
                        @blur="$v.form.code.$touch()"
                      >
                        <template v-slot:append>
                          <v-btn
                            fab
                            x-small
                            depressed
                            class="barcode-gen-btn"
                            @click="generateBarcode"
                            :title="
                              $t('generate_barcode') || 'Generate Barcode'
                            "
                          >
                           <v-icon x-small>fal fa-barcode-scan</v-icon>
                          </v-btn>
                        </template>
                      </TTextField>
                    </v-col>
                    <v-col cols="12" sm="6">
                      <TextSelect
                        label="category"
                        dense
                        :errorMessages="category_idError"
                        v-model="form.category_id"
                        @input="$v.form.category_id.$touch()"
                        @blur="$v.form.category_id.$touch()"
                        itemValue="id"
                        text="la_name"
                        :items="categories"
                      />
                      <span
                        class="ap-hint ap-hint--link"
                        @click="openQuickCategoryModal"
                        >+ {{ $t("add_category") }}</span
                      >
                    </v-col>
                    <v-col cols="12" sm="6">
                      <TextSelect
                        label="unit"
                        dense
                        v-model="form.size_id"
                        itemValue="id"
                        text="name"
                        :items="sizes"
                      />
                    </v-col>
                    <v-col cols="12" sm="6">
                      <TextSelect
                        label="supplier"
                        dense
                        v-model="form.vendor_id"
                        itemValue="id"
                        text="name"
                        :items="suppliers"
                      />
                    </v-col>
                  </v-row>

                  <div class="mt-3">
                    <TextArea
                      label="description"
                      dense
                      v-model="form.desc"
                      :rows="showOnlineMedia ? 6 : 3"
                    />
                    <div
                      v-if="showOnlineMedia"
                      class="text-caption grey--text mt-1"
                    >
                      {{ $t("product_desc_markdown_hint") }}
                    </div>
                  </div>
                </div>
              </div>
            </v-card-text>
          </v-card>

          <v-card class="ap-card mb-4" outlined>
            <v-card-title class="pb-1 product-card-title ap-card-title">
              <span class="ap-card-accent"></span>
              <span>{{ $t("price") }}</span>
              <span class="ap-card-subtitle">{{ $t("price_subtitle") }}</span>
            </v-card-title>
            <v-card-text class="pa-7 pt-3">
              <!-- currency -->
              <div class="section-label mb-2">{{ $t("currency") }}</div>
              <v-btn-toggle
                v-model="form.currency_id"
                mandatory
                dense
                class="ps-status-toggle mb-4"
              >
                <v-btn
                  v-for="cur in currencies"
                  :key="cur.id"
                  small
                  :value="cur.id"
                >
                  <v-avatar size="14" class="mr-1" v-if="cur.flag">
                    <v-img :src="cur.flag" />
                  </v-avatar>
                  {{ cur.code }}
                </v-btn>
              </v-btn-toggle>

              <!-- buy / sale prices -->
              <div class="row">
                <div class="col-6 pa-1">
                  <CurrencyInput
                    v-model="form.price_buy"
                    :suffix="currency"
                    label="cost"
                    dense
                    hide-details
                  />
                </div>
                <div class="col-6 pa-1">
                  <CurrencyInput
                    v-model="form.price_sale"
                    :suffix="currency"
                    label="price_sale"
                    dense
                    hide-details
                  />
                </div>
              </div>

              <!-- profit -->
              <v-row>
                <v-col cols="12" class="px-0">
                  <div class="v2-profit">
                    <div class="v2-profit__info">
                      <span class="v2-profit__label">{{
                        $t("profit_per_unit")
                      }}</span>
                      <span class="v2-profit__margin">{{ marginNote }}</span>
                    </div>
                    <span
                      class="v2-profit__value"
                      :class="
                        price_sale - price_buy >= 0
                          ? 'success--text'
                          : 'error--text'
                      "
                    >
                      {{ price_sale - price_buy >= 0 ? "+" : ""
                      }}{{ (price_sale - price_buy) | NumberFormat }}
                    </span>
                  </div>
                </v-col>
              </v-row>
              <div class="ap-hint mt-2">
                <v-icon x-small class="mr-1" color="grey"
                  >fal fa-info-circle</v-icon
                >
                {{ $t("margin_recalc_hint") }}
              </div>
            </v-card-text>
          </v-card>

          <!-- inventory -->
          <v-card class="ap-card mb-4" outlined>
            <v-card-title class="pb-1 product-card-title ap-card-title">
              <span class="ap-card-accent"></span>
              <span>{{ $t("inventory") }}</span>
              <span class="ap-card-subtitle">{{
                $t("inventory_subtitle")
              }}</span>
            </v-card-title>
            <v-card-text class="pa-7 pt-3">
              <div class="ap-toggle-row mb-4">
                <div class="v2-toggle-row__text">
                  <p class="v2-toggle-title">{{ $t("track_stock") }}</p>
                  <p class="v2-toggle-desc">{{ $t("track_stock_desc") }}</p>
                </div>
                <v-switch
                  v-model="form.track_stock"
                  inset
                  hide-details
                  color="primary"
                  :disabled="stockFieldsDisabled"
                />
              </div>

              <v-row v-if="form.track_stock && !stockFieldsDisabled" dense>
                <v-col cols="12" sm="4">
                  <CurrencyInput
                    v-model="form.stock"
                    label="stock"
                    dense
                    hide-details
                    :disabled="!edit_stock"
                  />
                </v-col>
                <v-col cols="12" sm="4">
                  <CurrencyInput
                    v-model="form.notify_stock"
                    label="stock_notify"
                    dense
                    hide-details
                    typeShow="number"
                  />
                </v-col>
                <v-col cols="12" sm="4">
                  <div class="ps-text-field">
                    <div class="pb-2">{{ $t("stock_value") }}</div>
                    <div class="ap-readonly-box">
                      {{ stockValue | NumberFormat }} {{ currency }}
                    </div>
                  </div>
                </v-col>
              </v-row>

              <div v-if="stockFieldsDisabled" class="ap-hint mt-2">
                <v-icon x-small class="mr-1" color="grey"
                  >fal fa-info-circle</v-icon
                >
                {{ $t("stock_managed_at_variant_level") }}
              </div>
            </v-card-text>
          </v-card>

          <!-- online media (only when store joined) -->
          <ProductOnlineMedia
            v-if="showOnlineMedia"
            ref="productOnlineMedia"
            :existing-images.sync="galleryExisting"
            :pending-files.sync="galleryPendingFiles"
            :video-url.sync="videoUrl"
            :is-online.sync="form.is_online"
          />
        </div>

        <!-- ============ RIGHT: AVAILABILITY / SETTINGS ============ -->
        <div class="col-ms-12 col-md-4 col-lg-4 col-xl-4">
          <!-- availability -->
          <v-card class="ap-card mb-4" outlined>
            <v-card-title class="pb-1 product-card-title ap-card-title">
              <span class="ap-card-accent"></span>
              <span>{{ $t("availability") }}</span>
            </v-card-title>
            <v-card-text class="pa-7 pt-3">
              <div class="ap-toggle-row mb-4">
                <div class="v2-toggle-row__text">
                  <p class="v2-toggle-title">{{ $t("online") }}</p>
                  <p class="v2-toggle-desc">{{ $t("online_product_desc") }}</p>
                </div>
                <v-switch
                  v-model="form.is_online"
                  inset
                  hide-details
                  color="primary"
                />
              </div>
              <div class="ap-toggle-row mb-4">
                <div class="v2-toggle-row__text">
                  <p class="v2-toggle-title mb-1">{{ $t("active_product") }}</p>
                  <v-chip
                    x-small
                    :color="form.is_active ? 'success' : 'grey'"
                    dark
                    class="rounded-lg"
                  >
                    {{ form.is_active ? $t("active") : $t("inactive") }}
                  </v-chip>
                </div>
                <v-switch
                  v-model="form.is_active"
                  inset
                  hide-details
                  color="primary"
                />
              </div>

              <div class="ap-checklist-title">{{ $t("before_you_save") }}</div>
              <div class="ap-checklist">
                <div
                  v-for="check in readinessChecklist"
                  :key="check.key"
                  class="ap-checklist__row"
                >
                  <span
                    class="ap-checklist__mark"
                    :class="{ 'ap-checklist__mark--ok': check.ok }"
                  >
                    <v-icon v-if="check.ok" size="10" color="white"
                      >fal fa-check</v-icon
                    >
                  </span>
                  <span
                    class="ap-checklist__label"
                    :class="{ 'ap-checklist__label--ok': check.ok }"
                    >{{ check.label }}</span
                  >
                </div>
              </div>
            </v-card-text>
          </v-card>

          <!-- settings: currency / expiry / inventory / variants / bundle / dynamic pricing -->
          <ProductChannelPrices
            v-model="form.channel_prices"
            :currency="currency"
            class="mb-4"
          />
          <div class="ap-settings-wrap">
            <ProductSettings
              ref="productSettings"
              :form="form"
              :currencies="currencies"
              :expiredPro.sync="expiredPro"
              :hasVariations.sync="hasVariations"
              :bundleItems.sync="bundleItems"
              :productId="productId"
              :edit_stock="edit_stock"
              :options="options"
              :notify_stockError="productSettingsNotifyError"
              :show-stock="false"
              :show-currency="false"
              @variations-updated="handleVariationsUpdated"
            />
          </div>
        </div>
      </v-row>

      <!-- ============ ACTION BAR ============ -->
      <v-card class="ap-card mt-2" outlined elevation="0">
        <v-card-actions class="pa-4">
          <p
            class="text-caption grey--text font-weight-bold mb-0 mr-auto hidden-sm-and-down"
          >
            {{ footerReadinessText }}
          </p>
          <v-btn
            large
            class="rounded-lg px-10 mr-2"
            style="width: 180px"
            color="error"
            outlined
            @click="showClearConfirm = true"
          >
            <v-icon left>fal fa-trash</v-icon>
            {{ $t("clear_form") }}
          </v-btn>
          <v-btn
            large
            class="rounded-lg px-10"
            style="width: 180px"
            color="primary"
            dark
            @click="submitForm(form)"
          >
            <v-icon left>fal fa-save</v-icon>
            {{ $t("update") }}
          </v-btn>
        </v-card-actions>
      </v-card>

      <!-- clear form confirm -->
      <app-custom-dialog
        v-model="showClearConfirm"
        title="confirm_clear_form"
        width="460"
        cancelText="no"
        submitText="yes"
        @submit="
          clearData();
          showClearConfirm = false;
        "
      >
        <div slot="activator">
          <p class="ma-0">{{ $t("are_you_sure_clear_form") }}</p>
        </div>
      </app-custom-dialog>

      <!-- Quick Add Category Dialog -->
      <app-custom-dialog
        v-model="showQuickCategoryModal"
        title="add_category"
        width="460"
        submitText="add"
        :submit-loading="quickCategorySubmitting"
        @submit="handleQuickCategorySubmit"
      >
        <div slot="activator" class="pt-1">
          <TTextField
            label="la_name"
            v-model="quickCategoryForm.la_name"
            :errorMessages="quickCategoryError"
            @input="quickCategoryTouched = true"
          />
          <TTextField
            class="mt-3"
            label="en_name"
            v-model="quickCategoryForm.en_name"
          />
        </div>
      </app-custom-dialog>

      <app-loading :overlay="overlay" />
    </section>
  </v-container>
</template>

<script>
import TTextField from "@/components/global/TextField";
import TextSelect from "@/components/global/TextSelect";
import TextArea from "@/components/global/TextArea";
import { mapGetters, mapActions } from "vuex";
import AppPreview from "@/components/products/preview";
import ProductVariations from "@/components/products/ProductVariations";
import ProductBundleBuilder from "@/components/products/ProductBundleBuilder.vue";
import ProductSettings from "@/components/products/ProductSettings.vue";
import ProductChannelPrices from "@/components/products/ProductChannelPrices.vue";
import ProductOnlineMedia from "@/components/products/ProductOnlineMedia.vue";
import { success, toast } from "@/controllers/alert";
import FileInput from "@/components/global/FileInput";
import { confirm } from "@/controllers/alert";
import { myMixin } from "@/mixin/main";
import DragDropUpload from "@/components/global/DragDropUpload.vue";
import { addProduct } from "@/validations/shop";
import CurrencyInput from "@/components/CurrencyInput";

// Same as add-product (POC): today in YYYY-MM-DD for expired_date default
const today = new Date(Date.now() - new Date().getTimezoneOffset() * 60000)
  .toISOString()
  .substr(0, 10);

/**
 * API returns expired_date as d-m-Y (e.g. 16-02-2026). Convert to YYYY-MM-DD
 * so Vuetify v-date-picker displays correctly.
 */
function parseExpiredDateFromApi(val) {
  if (!val) return null;
  if (/^\d{4}-\d{2}-\d{2}$/.test(val)) return val; // already Y-m-d
  const parts = String(val).split("-");
  if (parts.length !== 3) return val;
  const [d, m, y] = parts;
  const year = y.length === 2 ? `20${y}` : y;
  const month = m.padStart(2, "0");
  const day = d.padStart(2, "0");
  return `${year}-${month}-${day}`;
}

export default {
  mixins: [addProduct, myMixin],
  data: () => ({
    step: 1,
    imgFile: null,
    overlay: false,
    expiredPro: false,
    fist_set: true,
    hasVariations: false,
    activeLang: "LA",
    initialToppingIds: [],
    form: {
      code: "",
      name: "",
      name_en: "",
      name_th: "",
      name_cn: "",
      desc: "",
      stock: 0,
      expired_date: null,
      notify_stock: 10,
      track_stock: false,
      price_buy: 0,
      price_sale: 0,
      category_id: null,
      currency_id: 1,
      vendor_id: null,
      size_id: null,
      is_bundle: false,
      has_variations: false,
      is_active: true,
      dynamic_pricing: false,
      channel_prices: [],
      is_online: true,
    },
    options: {
      precision: 0,
    },
    edit_stock: false,
    productId: null,
    showClearConfirm: false,
    showQuickCategoryModal: false,
    quickCategorySubmitting: false,
    quickCategoryTouched: false,
    quickCategoryForm: {
      la_name: "",
      en_name: "",
    },
    currencies: [],
    bundleSearch: "",
    bundleSearchResult: null,
    bundleQty: 1,
    bundleItems: [],
    productSettingsNotifyError: "",
    storeJoinedOnline: false,
    branchJoinedOnline: false,
    galleryExisting: [],
    galleryPendingFiles: [],
    videoUrl: "",
  }),
  components: {
    TextSelect,
    TTextField,
    FileInput,
    TextArea,
    AppPreview,
    ProductVariations,
    ProductSettings,
    ProductChannelPrices,
    ProductOnlineMedia,
    DragDropUpload,
    ProductBundleBuilder,
    CurrencyInput,
  },
  computed: {
    quickCategoryError() {
      if (!this.quickCategoryTouched) return [];
      if (!this.quickCategoryForm.la_name || !this.quickCategoryForm.la_name.trim()) {
        return [this.$t("validation_required")];
      }
      return [];
    },
    ...mapGetters({
      category: "index/category",
      suppliersData: "index/suppliers",
      sizes: "index/sizes",
    }),
    categories() {
      if (
        this.category != null &&
        this.category != undefined &&
        this.category.length
      ) {
        return this.category;
      } else {
        return [];
      }
    },
    suppliers() {
      if (
        this.suppliersData != null &&
        this.suppliersData != undefined &&
        this.suppliersData.length
      ) {
        return this.suppliersData;
      } else {
        return [];
      }
    },

    rate() {
      // Simplified rate calculation - return 1 as default
      return 1;
    },

    price_buy() {
      // Simplified price calculation
      if (this.form && this.form.price_buy) {
        return Number(this.form.price_buy);
      } else {
        return 0;
      }
    },

    price_sale() {
      // Simplified price calculation
      if (this.form && this.form.price_sale) {
        return Number(this.form.price_sale);
      } else {
        return 0;
      }
    },

    currency() {
      if (!this.currencies || !Array.isArray(this.currencies)) return "";
      const found = this.currencies.find(
        (element) => element.id === this.form.currency_id,
      );
      if (found) {
        return found.code;
      }
      return "";
    },

    // Check if stock fields should be disabled
    stockFieldsDisabled() {
      return this.hasVariations || this.form.is_bundle;
    },

    showOnlineMedia() {
      return this.storeJoinedOnline && this.branchJoinedOnline;
    },

    // ---- redesigned header/name/checklist helpers (shared with add-product.vue) ----
    langTabs() {
      return ["LA", "EN", "TH", "CN"].map((code) => ({
        code,
        filled: !!(
          this.langFieldValue(code) && this.langFieldValue(code).trim()
        ),
      }));
    },
    activeLangKey() {
      return this.activeLang === "LA"
        ? "name"
        : `name_${this.activeLang.toLowerCase()}`;
    },
    currentNameValue: {
      get() {
        return this.form[this.activeLangKey] || "";
      },
      set(val) {
        this.$set(this.form, this.activeLangKey, val);
      },
    },
    headline() {
      return (
        (this.form.name && this.form.name.trim()) ||
        (this.form.name_en && this.form.name_en.trim()) ||
        (this.form.name_th && this.form.name_th.trim()) ||
        (this.form.name_cn && this.form.name_cn.trim()) ||
        this.$t("untitled_product")
      );
    },
    readinessChecklist() {
      return [
        {
          key: "name",
          ok: !!(this.form.name && this.form.name.trim()),
          label: this.$t("product_name"),
        },
        {
          key: "price",
          ok: this.price_sale > 0 && this.price_sale > this.price_buy,
          label: this.$t("check_price_above_cost"),
        },
        {
          key: "classification",
          ok: !!this.form.category_id && !!this.form.size_id,
          label: this.$t("check_category_unit"),
        },
        {
          key: "image",
          ok: !!this.imgFile,
          label: this.$t("product_image"),
        },
      ];
    },
    readinessTotal() {
      return this.readinessChecklist.length;
    },
    readinessCount() {
      return this.readinessChecklist.filter((c) => c.ok).length;
    },
    readinessPercent() {
      return (this.readinessCount / this.readinessTotal) * 100;
    },
    footerReadinessText() {
      if (this.readinessCount === this.readinessTotal) {
        return this.$t("everything_required_filled");
      }
      return this.$t("items_left_before_saving", {
        count: this.readinessTotal - this.readinessCount,
      });
    },
    stockValue() {
      const stock = Number(this.form.stock) || 0;
      return stock * this.price_buy;
    },
    marginNote() {
      if (!this.price_sale) {
        return this.$t("enter_sale_price_hint");
      }
      const percent =
        ((this.price_sale - this.price_buy) / this.price_sale) * 100;
      return this.$t("margin_label", { percent: percent.toFixed(1) });
    },
  },
  created() {
    this.fetchCategory();
    this.fetchVendor();
    this.fetchSize();
    this.fetchSingleProduct();
  },
  mounted() {
    this.fetExchange();
    this.checkRoute("edit_stock");
    this.checkStoreOnlineJoin();
  },
  watch: {
    expiredPro(val) {
      if (val) {
        // Only set to today when user turns switch ON and no date is set (don't overwrite loaded API value)
        const newDate = this.form.expired_date || today;
        this.form.expired_date = newDate;
      } else {
        this.form.expired_date = null;
      }
    },
    "form.expired_date": {
      handler(newVal) {
        // Keep expiredPro in sync: if user picks a date, treat as expiration enabled
        if (newVal && !this.expiredPro) {
          this.expiredPro = true;
        }
        if (!newVal && this.expiredPro) {
          this.expiredPro = false;
        }
      },
      immediate: false,
    },
    hasVariations(val) {
      if (val) {
        // When variants are enabled, set stock and notify_stock to 0
        this.form.stock = 0;
        this.form.notify_stock = 10;

        // Note: track_stock can remain enabled for variants - stock will be managed at variant level
        // this.form.track_stock = false; // Removed automatic disabling

        // When variants are enabled, disable bundle functionality (mutual exclusivity)
        this.form.is_bundle = false;
        this.bundleItems = [];

        // When variants are enabled, set default prices if they are not null
        if (this.form.price_buy && this.form.price_buy > 0) {
          // Keep the current price_buy as default cost
        }
        if (this.form.price_sale && this.form.price_sale > 0) {
          // Keep the current price_sale as default price
        }

        // Update existing variants with current parent product data
        this.$nextTick(() => {
          if (
            this.$refs.productSettings &&
            this.$refs.productSettings.$refs.productVariations &&
            typeof this.$refs.productSettings.$refs.productVariations
              .updateVariantsWithParentData === "function"
          ) {
            this.$refs.productSettings.$refs.productVariations.updateVariantsWithParentData();
          }
        });
      }
    },
    "form.price_buy"(val) {
      // Update variant costs when parent price_buy changes
      if (
        this.hasVariations &&
        this.$refs.productSettings &&
        this.$refs.productSettings.$refs.productVariations &&
        typeof this.$refs.productSettings.$refs.productVariations
          .updateVariantsWithParentData === "function"
      ) {
        this.$refs.productSettings.$refs.productVariations.updateVariantsWithParentData();
      }
    },
    "form.price_sale"(val) {
      // Update variant prices when parent price_sale changes
      if (
        this.hasVariations &&
        this.$refs.productSettings &&
        this.$refs.productSettings.$refs.productVariations &&
        typeof this.$refs.productSettings.$refs.productVariations
          .updateVariantsWithParentData === "function"
      ) {
        this.$refs.productSettings.$refs.productVariations.updateVariantsWithParentData();
      }
    },
    "form.code"(val) {
      // Update variant barcodes when parent code changes
      if (
        this.hasVariations &&
        this.$refs.productSettings &&
        this.$refs.productSettings.$refs.productVariations &&
        typeof this.$refs.productSettings.$refs.productVariations
          .updateVariantsWithParentData === "function"
      ) {
        this.$refs.productSettings.$refs.productVariations.updateVariantsWithParentData();
      }
    },
    "form.track_stock"(val) {
      if (!val) {
        this.form.stock = 0;
        this.form.notify_stock = 10;
      }
    },
    bundleItems: {
      handler(val) {
        // Automatically set is_bundle flag based on whether there are bundle items
        this.form.is_bundle = val && val.length > 0;

        // When bundle is enabled, set stock and notify_stock to 0
        if (this.form.is_bundle) {
          this.form.stock = 0;
          this.form.notify_stock = 0;
          // When bundle is enabled, disable track_stock as stock is managed at bundle level
          this.form.track_stock = false;
          // When bundle is enabled, disable variants functionality (mutual exclusivity)
          this.hasVariations = false;
          this.form.has_variations = false;

          // When bundle is enabled, disable expiration functionality
          this.expiredPro = false;
          this.form.expired_date = null;
        }
      },
      deep: true,
    },

    // Watch for direct changes to form.is_bundle
    "form.is_bundle"(val) {
      if (val) {
        // When bundle is enabled, disable variants
        this.hasVariations = false;
        this.form.has_variations = false;
        this.form.stock = 0;
        this.form.notify_stock = 0;
        this.form.track_stock = false;

        // When bundle is enabled, disable expiration functionality
        this.expiredPro = false;
        this.form.expired_date = null;
      }
    },

    // Watch for direct changes to form.has_variations
    "form.has_variations"(val) {
      if (val) {
        // When variants are enabled, disable bundle and dynamic pricing
        this.form.is_bundle = false;
        this.bundleItems = [];
        this.form.stock = 0;
        this.form.notify_stock = 10;
        this.form.dynamic_pricing = false;
        // Note: track_stock can remain enabled for variants - stock will be managed at variant level
        // this.form.track_stock = false; // Removed automatic disabling
      }
    },
    // Watch for hasVariations changes to disable dynamic_pricing
    hasVariations(val) {
      if (val) {
        this.form.dynamic_pricing = false;
      }
    },
  },
  methods: {
    ...mapActions({
      fetch: "index/fetch",
    }),
    langFieldValue(code) {
      const key = code === "LA" ? "name" : `name_${code.toLowerCase()}`;
      return this.form[key];
    },
    async fetchSize() {
      const body = {
        that: this,
        url: "product_size",
        commit: "SET_SIZES",
      };
      await this.fetch(body);
    },
    async fetchVendor() {
      const body = {
        that: this,
        url: "suppliers",
        commit: "SET_SUPPLIERS",
      };
      await this.fetch(body);
    },
    async fetchCategory() {
      const body = {
        that: this,
        url: "categories",
        commit: "SET_CATEGORY",
      };
      await this.fetch(body);
    },
    openQuickCategoryModal() {
      this.quickCategoryForm = { la_name: "", en_name: "" };
      this.quickCategoryTouched = false;
      this.showQuickCategoryModal = true;
    },
    async handleQuickCategorySubmit() {
      this.quickCategoryTouched = true;
      if (!this.quickCategoryForm.la_name || !this.quickCategoryForm.la_name.trim()) {
        return;
      }
      this.quickCategorySubmitting = true;
      try {
        const payload = {
          la_name: this.quickCategoryForm.la_name.trim(),
          en_name: this.quickCategoryForm.en_name ? this.quickCategoryForm.en_name.trim() : null,
          is_online: 1,
          is_cooking: 1,
          number: 0,
        };
        const { data } = await this.$http.post("categories", payload);
        if (data && data.status) {
          this.showQuickCategoryModal = false;
          await this.fetchCategory();
          const created =
            this.categories.find((c) => c.id === data.data?.id) || data.data;
          this.form.category_id = created;
          if (this.$v && this.$v.form && this.$v.form.category_id) {
            this.$v.form.category_id.$touch();
          }
          await success(this, data.message);
        } else {
          await toast(this, data?.message || "error_occurred", "error");
        }
      } catch (error) {
        console.error("quick category create error", error);
        const message =
          error?.response?.data?.message ||
          error?.message ||
          this.$t("error_occurred") ||
          "Error";
        if (this.$toast) {
          this.$toast.error(message);
        } else {
          await toast(this, message, "error");
        }
      } finally {
        this.quickCategorySubmitting = false;
      }
    },
    async submitForm(form) {
      try {
        // Enforce mutual exclusivity before form submission
        this.enforceMutualExclusivity();

        this.$v.form.$touch();
        if (this.$v.form.$invalid) {
          await toast(this, "validation_required", "error");
          return;
        }
        const { id } = this.$route.params;
        this.overlay = true;
        let formData = new FormData();
        formData.append("code", form.code);
        formData.append("name", form.name);
        formData.append("name_en", form.name_en || "");
        formData.append("name_th", form.name_th || "");
        formData.append("name_cn", form.name_cn || "");
        let options = [];
        let variants = [];
        if (
          this.hasVariations &&
          this.$refs.productSettings &&
          this.$refs.productSettings.$refs.productVariations
        ) {
          // Safely access options and variants with fallbacks
          options =
            this.$refs.productSettings.$refs.productVariations.options || [];
          variants =
            this.$refs.productSettings.$refs.productVariations.variants || [];
        }
        // If hasVariations is false, send variants as an empty array
        if (!this.hasVariations) {
          variants = [];
        }
        // If variants exist, set price_buy and price_sale to 0
        if (variants.length > 0) {
          form.price_buy = 0;
          form.price_sale = 0;
          form.stock = 0;
        }

        // If product is a bundle, set stock and notify_stock to 0
        if (form.is_bundle) {
          form.stock = 0;
          form.notify_stock = 0;
        }
        formData.append("desc", form.desc);
        if (this.expiredPro) {
          formData.append("expired_date", form.expired_date);
        }
        formData.append("notify_stock", form.notify_stock);
        formData.append("price_buy", form.price_buy);
        formData.append("price_sale", form.price_sale);
        formData.append("stock", this.edit_stock ? form.stock : 0);
        formData.append("track_stock", form.track_stock ? 1 : 0);
        // check if category_id is object
        const category_id = Help.getId(form?.category_id) || null;
        const vendor_id = Help.getId(form?.vendor_id);
        const size_id = form?.size_id?.id ? form.size_id.id : form.size_id;
        formData.append("category_id", category_id);
        formData.append("vendor_id", vendor_id);
        formData.append("size_id", size_id);
        formData.append("currency_id", form.currency_id || null);
        // Add options and variants
        formData.append("options", JSON.stringify(options));
        formData.append("variants", JSON.stringify(variants));
        // Set has_variations based on whether variants exist
        formData.append(
          "has_variations",
          variants && variants.length > 0 ? 1 : 0,
        );
        formData.append(
          "has_topping",
          form.topping_ids && form.topping_ids.length > 0 ? 1 : 0,
        );
        formData.append(
          "topping_ids",
          JSON.stringify(form.topping_ids || []),
        );
        // Bundle payload
        formData.append("is_bundle", form.is_bundle ? 1 : 0);
        if (form.is_bundle) {
          const payload = this.bundleItems.map((b) => ({
            product_id: b.product_id,
            quantity: b.quantity,
          }));
          formData.append("bundle_products", JSON.stringify(payload));
        }

        // Add is_active status
        formData.append("is_active", form.is_active ? 1 : 0);
        formData.append("dynamic_pricing", form.dynamic_pricing ? 1 : 0);
        formData.append(
          "channel_prices",
          JSON.stringify(form.channel_prices || []),
        );

        if (this.showOnlineMedia) {
          if (
            this.$refs.productOnlineMedia &&
            !this.$refs.productOnlineMedia.validate()
          ) {
            this.overlay = false;
            return;
          }

          const keepIds = this.$refs.productOnlineMedia
            ? this.$refs.productOnlineMedia.getGalleryKeepIds()
            : this.galleryExisting.map((row) => row.id);
          formData.append("gallery_keep_ids", JSON.stringify(keepIds));
          this.galleryPendingFiles.forEach((file) => {
            formData.append("gallery_files[]", file);
          });
          formData.append("video_url", this.videoUrl || "");
          formData.append("is_online", form.is_online ? 1 : 0);
        }

        // Add image file if exists
        if (this.imgFile && typeof this.imgFile === "object") {
          formData.append("file", this.imgFile);
        }

        const { data } = await this.$http.post(
          `products_update/${id}`,
          formData,
          Help.headerFile(),
        );
        const initialToppingIds = (this.initialToppingIds || []).map(Number).sort().join(",");
        const currentToppingIds = (form.topping_ids || []).map(Number).sort().join(",");
        const toppingsChanged = initialToppingIds !== currentToppingIds;

        if (data.status && toppingsChanged) {
          try {
            await this.$http.put(`products/${id}/toppings`, {
              topping_ids: form.topping_ids || [],
            });
          } catch (toppingError) {
            console.error("Failed to sync toppings:", toppingError);
          }
        }
        this.overlay = false;
        if (data.status) {
          // this.clearData(); // Do not clear the form after update
          await success(this, data.message);
          // Refresh the product data to show updated values
          await this.fetchSingleProduct();
        } else if (data && data.message) {
          await toast(this, data.message, "error");
        } else {
          await toast(this, "error_occurred", "error");
        }
      } catch (error) {
        this.overlay = false;
        await toast(this, "error_occurred", "error");
      }
    },
    async fetchSingleProduct() {
      try {
        this.overlay = true;
        const { id } = this.$route.params;
        this.productId = id;
        const { data } = await this.$http.get(`products/${id}`);
        if (data.status) {
          const _data = data.data;
          this.$set(this.form, "code", _data.code);
          this.$set(this.form, "name", _data.name);
          this.$set(this.form, "name_en", _data.name_en || "");
          this.$set(this.form, "name_th", _data.name_th || "");
          this.$set(this.form, "name_cn", _data.name_cn || "");

          this.$set(this.form, "desc", _data.desc);
          this.$set(this.form, "stock", _data.stock);
          this.$set(this.form, "notify_stock", _data.notify_stock);
          this.$set(this.form, "price_buy", _data.price_buy);
          this.$set(this.form, "price_sale", _data.price_sale);
          this.$set(this.form, "tax_amount", _data.tax_amount || 0);
          this.$set(this.form, "vendor_id", _data?.vendor?.id || null);
          this.$set(this.form, "category_id", _data?.category?.id || null);
          this.$set(this.form, "size_id", _data?.size?.id || null);
          this.$set(this.form, "category", _data?.category || null);
          this.$set(this.form, "size", _data?.size || null);
          /* API often returns currency_id null; mixin requires currency_id — only overwrite when set */
          if (_data.currency_id != null && _data.currency_id !== "") {
            this.$set(this.form, "currency_id", _data.currency_id);
          }
          this.$set(
            this.form,
            "track_stock",
            typeof _data.track_stock !== "undefined"
              ? !!_data.track_stock
              : false,
          );
          this.$set(
            this.form,
            "is_active",
            typeof _data.is_active !== "undefined" ? !!_data.is_active : true,
          );
          this.$set(
            this.form,
            "dynamic_pricing",
            typeof _data.dynamic_pricing !== "undefined"
              ? !!_data.dynamic_pricing
              : false,
          );
          this.$set(
            this.form,
            "channel_prices",
            Array.isArray(_data.channel_prices)
              ? _data.channel_prices.map((item) => ({
                  price_channel_id: item.price_channel_id,
                  price: item.price,
                  is_active:
                    item.is_active === false || item.is_active === 0 ? 0 : 1,
                }))
              : [],
          );
          this.$set(
            this.form,
            "is_online",
            typeof _data.is_online !== "undefined" ? !!_data.is_online : true,
          );
          this.galleryExisting = Array.isArray(_data.images)
            ? _data.images
            : [];
          this.galleryPendingFiles = [];
          this.videoUrl = _data.video_url || "";
          // Auto-check variant toggle if variants/options exist
          if (
            (_data.variants && _data.variants.length > 0) ||
            (_data.options && _data.options.length > 0)
          ) {
            this.hasVariations = true;
          } else {
            this.hasVariations = false;
          }
          // Assign options and variants directly from API response
          this.$set(this.form, "options", _data.options || []);
          this.$set(this.form, "variants", _data.variants || []);
          this.fist_set = false;
          if (_data.expired_date) {
            const normalized = parseExpiredDateFromApi(_data.expired_date);
            this.$set(this.form, "expired_date", normalized);
            this.expiredPro = true;
          }
          this.$set(this.form, "img", _data.img);
          // bundle
          this.$set(this.form, "is_bundle", !!_data.is_bundle);
          const toppings = Array.isArray(_data.toppings) ? _data.toppings : [];
          this.$set(this.form, "toppings", toppings);
          this.$set(
            this.form,
            "topping_ids",
            toppings.map((t) => t.id)
          );
          this.initialToppingIds = toppings.map((t) => t.id);
          const bundle = Array.isArray(_data.bundle_items)
            ? _data.bundle_items
            : [];
          this.bundleItems = bundle.map((b) => ({
            product_id: b.product_id,
            quantity: b.quantity,
            product: b.product || null,
          }));

          // Enforce mutual exclusivity after loading product data
          this.enforceMutualExclusivity();

          if (_data.img) {
            // Check if the image is already a full URL (http/https)
            if (
              _data.img.startsWith("http://") ||
              _data.img.startsWith("https://")
            ) {
              this.imgFile = _data.img;
            } else {
              // If it's a relative path, prepend the base URL
              this.imgFile =
                process.env.VUE_APP_BASE_URL + "/uploads/" + _data.img;
            }
          } else {
            this.imgFile = null;
          }
          this.syncDefaultCurrency();
          this.overlay = false;
        }
        this.overlay = false;
      } catch (error) {
        this.overlay = false;
      }
    },

    /** Keep currency_id valid for vuelidate when product/store uses base currency only (API null). */
    syncDefaultCurrency() {
      const missing =
        this.form.currency_id === null ||
        this.form.currency_id === undefined ||
        this.form.currency_id === "";
      if (
        !missing ||
        !Array.isArray(this.currencies) ||
        !this.currencies.length
      ) {
        return;
      }
      this.$set(this.form, "currency_id", this.currencies[0].id);
    },

    base_price(value, _rate) {
      // Since backend now stores original prices, no conversion needed
      if (value && value > 0) {
        return Number(value);
      } else {
        return 0;
      }
    },

    async clearForm() {
      const { isConfirmed } = await confirm({
        props: this,
        text: "want_to_clear_form",
      });

      if (isConfirmed) {
        this.clearData();
        this.imgFile = null;
      }
    },
    clearData() {
      this.form = {
        code: "",
        img: "",
        name: "",
        desc: "",
        stock: 0,
        expired_date: today,
        notify_stock: 10,
        price_buy: 0,
        tax_amount: 0,
        price_sale: 0,
        vendor_id: null,
        category_id: null,
        size_id: null,
        category: {},
        size: {},
        track_stock: false,
        currency_id: 1,
        is_bundle: false,
        has_variations: false,
        dynamic_pricing: false,
        channel_prices: [],
      };
      this.imgFile = null;
      this.activeLang = "LA";
      this.expiredPro = false;
      this.hasVariations = false;
      this.bundleItems = [];
      if (
        this.$refs.productSettings &&
        this.$refs.productSettings.$refs.productVariations &&
        typeof this.$refs.productSettings.$refs.productVariations.clearData ===
          "function"
      ) {
        this.$refs.productSettings.$refs.productVariations.clearData();
      }
    },

    handleVariationsUpdated(variations) {
      // Handle variations updated event
      // TODO: Handle the variations updated event if needed
    },
    async checkStoreOnlineJoin() {
      try {
        const { data } = await this.$http.post("check_join");
        this.storeJoinedOnline =
          !!data?.data && data.data.status === "approved";
        this.branchJoinedOnline =
          !!data?.data && Number(data.data.branch_is_online) === 1;
      } catch (error) {
        this.storeJoinedOnline = false;
        this.branchJoinedOnline = false;
      }
    },

    async fetExchange() {
      try {
        const { data } = await this.$http.get("get_currency");
        this.currencies = data.data;
        this.syncDefaultCurrency();
      } catch (error) {
        // Handle error silently
      }
    },

    async checkRoute(role) {
      const _result = await this.hasRole(role);
      this.edit_stock = _result;
    },

    addBundleItem() {
      if (!this.bundleSearchResult) return;

      // Check if product already exists in bundle
      const exists = this.bundleItems.find(
        (x) => x.product_id === this.bundleSearchResult.id,
      );
      if (exists) return;

      // Check currency match for bundle products
      if (this.form.is_bundle && this.bundleItems.length > 0) {
        const firstProductCurrency = this.bundleItems[0].product.currency_id;
        if (this.bundleSearchResult.currency_id !== firstProductCurrency) {
          // Show warning about currency mismatch
          this.$swal({
            title:
              this.$t("currency_mismatch_warning") ||
              "Currency Mismatch Warning",
            text:
              this.$t("currency_mismatch_message") ||
              "Cannot add products with different currencies to a bundle. All bundle products must have the same currency.",
            icon: "warning",
            confirmButtonText: this.$t("ok") || "OK",
          });
          return;
        }
      }

      // Add product to bundle
      this.bundleItems.push({
        product_id: this.bundleSearchResult.id,
        quantity: this.bundleQty || 1,
        product: this.bundleSearchResult,
      });

      // Calculate total price_buy for bundle
      this.calculateBundlePrice();

      this.bundleSearchResult = null;
      this.bundleSearch = "";
      this.bundleQty = 1;
    },
    removeBundleItem(idx) {
      this.bundleItems.splice(idx, 1);

      // Recalculate total price_buy for bundle after removal
      this.calculateBundlePrice();
    },

    // Calculate total price_buy and price_sale for bundle products
    calculateBundlePrice() {
      if (this.form.is_bundle && this.bundleItems.length > 0) {
        let totalPriceBuy = 0;
        let totalPriceSale = 0;

        this.bundleItems.forEach((item) => {
          const productPriceBuy = parseFloat(item.product.price_buy || 0);
          const productPriceSale = parseFloat(item.product.price_sale || 0);
          const quantity = parseInt(item.quantity || 1);

          // Since backend now stores original prices, no conversion needed
          totalPriceBuy += productPriceBuy * quantity;
          totalPriceSale += productPriceSale * quantity;
        });

        // Update the parent product's price_buy and price_sale
        this.form.price_buy = totalPriceBuy;
        this.form.price_sale = totalPriceSale;
      }
    },

    // Get current exchange rate for the selected currency
    getCurrentExchangeRate() {
      if (!this.currencies || !Array.isArray(this.currencies)) return 1;
      const selectedCurrency = this.currencies.find(
        (currency) => currency.id === this.form.currency_id,
      );
      if (selectedCurrency && selectedCurrency.exchange_rate) {
        return Number(selectedCurrency.exchange_rate);
      }
      return 1;
    },

    onFileSelected(file) {
      // File selected
    },
    onFileRemoved() {
      // File removed
    },

    async fetchExpiredPro() {
      const body = {
        that: this,
        url: "expired_pro",
        commit: "SET_EXPIRED_PRO",
      };
      await this.fetch(body);
    },

    // Enforce mutual exclusivity between variants and bundles
    enforceMutualExclusivity() {
      if (this.form.has_variations && this.form.is_bundle) {
        // If both are enabled, prioritize variants and disable bundle
        this.form.is_bundle = false;
        this.bundleItems = [];
      }
    },

    // Handle manual toggle of has_variations
    onHasVariationsToggle() {
      if (this.form.has_variations) {
        // When variants are enabled, disable bundle
        this.form.is_bundle = false;
        this.bundleItems = [];
        this.form.stock = 0;
        this.form.notify_stock = 10;
        this.form.track_stock = false;
      }
    },

    // Handle manual toggle of is_bundle
    onIsBundleToggle() {
      if (this.form.is_bundle) {
        // When bundle is enabled, disable variants
        this.hasVariations = false;
        this.form.has_variations = false;
        this.form.stock = 0;
        this.form.notify_stock = 0;
        this.form.track_stock = false;
      }
    },

    generateBarcode() {
      const today = new Date().toISOString().substr(0, 8).replace(/-/g, "");
      const randomDigits = Math.floor(Math.random() * 1000)
        .toString()
        .padStart(3, "0");
      this.form.code = `${today}${randomDigits}`;
    },
  },
};
</script>
<style scoped>
.product-page-title {
  font-size: 20px;
  font-weight: 900;
}

.product-card-title {
  font-size: 14px;
  font-weight: 500;
}
.v-card__title.product-card-title {
  font-weight: 500;
}

.section-label,
.v2-label,
.ap-card ::v-deep .ps-text-field > .pb-2,
.ap-card ::v-deep .ps-text-select > .pb-2,
.ap-card ::v-deep .ps-text-area > .pb-2 {
  font-size: 13.5px !important;
  font-weight: 700 !important;
  color: #1e293b !important;
  margin-bottom: 6px;
}

.required-mark {
  color: #ef4444;
  font-weight: 700;
}

/* header row: kicker + headline + readiness progress */
.ap-header-row {
  gap: 20px;
}
.ap-kicker {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.11em;
  text-transform: uppercase;
  color: var(--v-primary-base, #5b39c9);
}
.ap-progress {
  min-width: 200px;
}
.ap-progress__labels {
  font-size: 11.5px;
  color: #94a3b8;  
  margin-bottom: 4px;
}

/* top-level section cards: slightly larger, consistent radius than Vuetify's rounded-lg */
.ap-card {
  border-radius: 16px !important;
  border: 1px solid #e2e8f0 !important;
  box-shadow: none !important;
  overflow: hidden;
}
.theme--dark .ap-card,
.theme--dark.ap-card {
  background: #1e1e2d !important;
  border: 1px solid rgba(255, 255, 255, 0.08) !important;
}

/* Advanced settings panel (ProductSettings + ProductBundleBuilder) — CSS-only chrome so it
   reads consistently with the other section cards on this page. ProductSettings.vue itself
   (variants/bundle/expiry/pricing logic) is untouched; only its rendered DOM is restyled. */
.ap-settings-wrap ::v-deep > .v-card {
  border-radius: 16px !important;
  border: 1px solid #e2e8f0 !important;
  box-shadow: none !important;
}
.theme--dark .ap-settings-wrap ::v-deep > .v-card {
  background: #1e1e2d !important;
  border: 1px solid rgba(255, 255, 255, 0.08) !important;
}
.ap-settings-wrap ::v-deep > .v-card > .v-card__title {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 14px;
  font-weight: 500;
}
.ap-settings-wrap ::v-deep > .v-card > .v-card__title .ap-card-accent {
  width: 3px;
  height: 15px;
  border-radius: 2px;
  background: var(--v-primary-base, #5b39c9);
  display: inline-block;
}
.ap-settings-wrap ::v-deep > .v-card > .v-card__title .ap-card-subtitle {
  font-size: 11.5px;
  font-weight: 400;
  color: #94a3b8;
}
/* flatten the inner feature cards (currency / expiry / inventory / variants / bundle /
   pricing) into simple plain rows instead of individually boxed cards — no divider
   lines between them.
   Vuetify 2 renders the `outlined` prop as the `v-sheet--outlined` mixin class, not
   `v-card--outlined` — this must match that class or the override never applies. */
.ap-settings-wrap ::v-deep .v-sheet--outlined .v-sheet--outlined {
  border: none !important;
  border-radius: 0 !important;
  box-shadow: none !important;
  margin: 0 !important;
  background: transparent !important;
}
.theme--dark .ap-settings-wrap ::v-deep .v-sheet--outlined .v-sheet--outlined {
  border: none !important;
  background: transparent !important;
}
.ap-settings-wrap
  ::v-deep
  .v-sheet--outlined
  .v-sheet--outlined
  > .v-card__title {
  font-size: 13px;
  font-weight: 900;
  padding: 14px 4px 6px;
}
.ap-settings-wrap
  ::v-deep
  .v-sheet--outlined
  .v-sheet--outlined
  > .v-card__text {
  padding: 14px 4px;
}
/* Currency and Inventory inside ProductSettings have their own
   dedicated cards on the left panel (Price, Inventory) — hide these duplicate rows */
.ap-settings-wrap ::v-deep .product-settings-currency,
.ap-settings-wrap ::v-deep .product-settings-stock {
  display: none !important;
}

/* Input Fields Appearance (Ref: /manage-promotions/new) */
.ap-card ::v-deep .v-text-field--outlined .v-input__slot,
.ap-card ::v-deep .v-textarea--outlined .v-input__slot {
  background: #f8fafc !important;
  border-radius: 12px !important;
  min-height: 44px;
  border: 1px solid #e2e8f0 !important;
  transition: background-color 0.15s ease, border-color 0.15s ease, box-shadow 0.15s ease;
}

.ap-card ::v-deep .v-text-field--outlined fieldset,
.ap-card ::v-deep .v-textarea--outlined fieldset {
  border: none !important;
}

.ap-card ::v-deep .v-text-field--outlined.v-input--is-focused .v-input__slot,
.ap-card ::v-deep .v-textarea--outlined.v-input--is-focused .v-input__slot {
  background: #ffffff !important;
  border-color: var(--v-primary-base, #5b39c9) !important;
  box-shadow: 0 0 0 3px rgba(91, 57, 201, 0.12) !important;
}

.ap-card ::v-deep .v-text-field--outlined .v-input__slot input,
.ap-card ::v-deep .v-select__selection,
.ap-card ::v-deep .v-textarea--outlined .v-input__slot textarea {
  font-size: 14px;
  font-weight: 600;
  color: #0f172a;
}

.ap-card ::v-deep .v-input__append-inner {
  margin-top: 0 !important;
  margin-bottom: 0 !important;
  align-self: center !important;
}

/* Barcode generate button */
.barcode-gen-btn {
  width: 28px !important;
  height: 28px !important;
  background: rgba(91, 57, 201, 0.08) !important;
  color: var(--v-primary-base, #5b39c9) !important;
}
.barcode-gen-btn:hover {
  background: rgba(91, 57, 201, 0.16) !important;
}

/* read-only stock-value box (Inventory card) */
.ap-readonly-box {
  display: flex;
  align-items: center;
  padding: 0 14px;
  border-radius: 12px;
  border: 1px dashed rgba(15, 23, 42, 0.16);
  background: #f8fafc;
  font-size: 14px;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  color: #475569;
  min-height: 44px;
  box-sizing: border-box;
}
::v-deep .theme--dark .ap-readonly-box {
  border-color: rgba(255, 255, 255, 0.16);
  background: #262638;
  color: rgba(226, 232, 240, 0.9);
}

/* card title with accent bar + subtitle */
.ap-card-title {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
  font-weight: 900;
}
.ap-card-accent {
  width: 3px;
  height: 15px;
  border-radius: 2px;
  background: var(--v-primary-base, #5b39c9);
  display: inline-block;
}
.ap-card-subtitle {
  font-size: 11.5px;
  font-weight: 400;
  color: #94a3b8;
}

.ap-hint {
  font-size: 10.5px;
  color: #94a3b8;
  display: inline-flex;
  align-items: center;
}

/* identity: compact image + name side by side */
.ap-identity-row {
  display: flex;
  align-items: flex-start;
  gap: 20px;
  flex-wrap: wrap;
}
.ap-identity-row__image {
  width: 220px;
  flex: 0 0 220px;
}
.ap-identity-row__name {
  flex: 1 1 320px;
  min-width: 280px;
}
@media (max-width: 599px) {
  .ap-identity-row__image {
    width: 100%;
    flex: 1 1 100%;
  }
}

/* language tab pills */
.ap-lang-pills {
  display: inline-flex;
  gap: 4px;
  padding: 3px;
  border-radius: 8px;
  background: rgba(91, 57, 201, 0.06);
  border: 1px solid rgba(15, 23, 42, 0.08);
  width: fit-content;
}
.ap-lang-pill {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 11px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 700;
  cursor: pointer;
  user-select: none;
  color: #64748b;
  transition: color 0.15s, background 0.15s;
}
.ap-lang-pill--active {
  color: var(--v-primary-base, #5b39c9);
  background: rgba(91, 57, 201, 0.12);
}
.ap-lang-dot {
  width: 5px;
  height: 5px;
  border-radius: 999px;
  background: #cbd5e1;
  display: block;
}
.ap-lang-dot--filled {
  background: var(--v-primary-base, #5b39c9);
}

/* before-you-save checklist */
.ap-checklist-title {
  font-size: 10.5px;
  letter-spacing: 0.09em;
  text-transform: uppercase;
  color: #94a3b8;
  font-weight: 700;
  margin-bottom: 8px;
}
.ap-checklist {
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.ap-checklist__row {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 12.5px;
}
.ap-checklist__mark {
  width: 16px;
  height: 16px;
  flex: 0 0 16px;
  border-radius: 999px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid #cbd5e1;
}
.ap-checklist__mark--ok {
  background: var(--v-primary-base, #5b39c9);
  border-color: var(--v-primary-base, #5b39c9);
}
.ap-checklist__label {
  color: #94a3b8;
}
.ap-checklist__label--ok {
  color: #334155;
  font-weight: 600;
}

/* v2 toggle row */
.v2-toggle-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  border: 1px solid rgba(15, 23, 42, 0.08);
  border-radius: 12px;
  padding: 10px 14px;
}
/* borderless variant: switch + label inline, no box (used in Availability card) */
.ap-toggle-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}
.ap-hint--link {
  color: var(--v-primary-base, #5b39c9);
  cursor: pointer;
  font-weight: 700;
}
.ap-hint--link:hover {
  text-decoration: underline;
}
.v2-toggle-row__text {
  min-width: 0;
}
.v2-toggle-title {
  margin: 0;
  font-size: 13px;
  font-weight: 500;
  line-height: 1.3;
}
.v2-toggle-desc {
  margin: 2px 0 0;
  font-size: 11px;
  color: #94a3b8;
  font-weight: 400;
}

/* v2 profit bar */
.v2-profit {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  background: rgba(91, 57, 201, 0.08);
  border-radius: 12px;
  padding: 10px 14px;
}
.v2-profit__info {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}
.v2-profit__label {
  font-size: 10.5px;
  font-weight: 900;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: #64748b;
}
.v2-profit__margin {
  font-size: 11px;
  font-weight: 600;
  color: #94a3b8;
}
.v2-profit__value {
  font-size: 17px;
  font-weight: 900;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
}

/* dark mode tweaks */
::v-deep .theme--dark .v2-label,
::v-deep .theme--dark .v2-toggle-desc,
::v-deep .theme--dark .ap-progress__labels,
::v-deep .theme--dark .ap-card-subtitle,
::v-deep .theme--dark .ap-hint,
::v-deep .theme--dark .ap-checklist-title,
::v-deep .theme--dark .ap-checklist__label {
  color: rgba(148, 163, 184, 0.9);
}
::v-deep .theme--dark .v2-toggle-row,
::v-deep .theme--dark .ap-lang-pills {
  border-color: rgba(255, 255, 255, 0.12);
}
::v-deep .theme--dark .ap-lang-pills {
  background: rgba(139, 111, 240, 0.12);
}
::v-deep .theme--dark .ap-lang-pill {
  color: rgba(148, 163, 184, 0.9);
}
::v-deep .theme--dark .ap-lang-pill--active,
::v-deep .theme--dark .ap-kicker {
  color: #8b6ff0;
}
::v-deep .theme--dark .ap-lang-pill--active {
  background: rgba(139, 111, 240, 0.2);
}
::v-deep .theme--dark .ap-card-accent,
::v-deep .theme--dark .ap-checklist__mark--ok {
  background: #8b6ff0;
}
::v-deep .theme--dark .ap-checklist__mark--ok {
  border-color: #8b6ff0;
}
::v-deep .theme--dark .ap-checklist__label--ok {
  color: #e2e8f0;
}
::v-deep .theme--dark .v2-profit {
  background: rgba(91, 57, 201, 0.2);
}
::v-deep .theme--dark .v2-profit__label,
::v-deep .theme--dark .v2-profit__margin {
  color: rgba(148, 163, 184, 0.9);
}

/* Dark Mode Input Fields Appearance */
::v-deep .theme--dark .section-label,
::v-deep .theme--dark .ps-text-field > .pb-2,
::v-deep .theme--dark .ps-text-select > .pb-2,
::v-deep .theme--dark .ps-text-area > .pb-2 {
  color: rgba(255, 255, 255, 0.88) !important;
}

::v-deep .theme--dark.ap-card .v-text-field--outlined .v-input__slot,
::v-deep .theme--dark.ap-card .v-textarea--outlined .v-input__slot,
::v-deep .theme--dark .ap-card .v-text-field--outlined .v-input__slot,
::v-deep .theme--dark .ap-card .v-textarea--outlined .v-input__slot {
  background: #262638 !important;
  border-color: rgba(255, 255, 255, 0.12) !important;
}

::v-deep .theme--dark.ap-card .v-text-field--outlined.v-input--is-focused .v-input__slot,
::v-deep .theme--dark.ap-card .v-textarea--outlined.v-input--is-focused .v-input__slot,
::v-deep .theme--dark .ap-card .v-text-field--outlined.v-input--is-focused .v-input__slot,
::v-deep .theme--dark .ap-card .v-textarea--outlined.v-input--is-focused .v-input__slot {
  background: #262638 !important;
  border-color: #8b6ff0 !important;
  box-shadow: 0 0 0 3px rgba(139, 111, 240, 0.2) !important;
}

::v-deep .theme--dark.ap-card .v-text-field--outlined .v-input__slot input,
::v-deep .theme--dark.ap-card .v-select__selection,
::v-deep .theme--dark.ap-card .v-textarea--outlined .v-input__slot textarea,
::v-deep .theme--dark .ap-card .v-text-field--outlined .v-input__slot input,
::v-deep .theme--dark .ap-card .v-select__selection,
::v-deep .theme--dark .ap-card .v-textarea--outlined .v-input__slot textarea {
  color: #f1f5f9 !important;
}

::v-deep .theme--dark .barcode-gen-btn {
  background: rgba(139, 111, 240, 0.15) !important;
  color: #c4b5fd !important;
}
</style>
